import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Quote, Award, ShieldCheck } from 'lucide-react';
import { fetchDiocesan } from '@/services/api';

interface DiocesanInfo {
  photo: string;
  name: string;
}

const BishopWelcome = () => {
  const [diocesan, setDiocesan] = useState<DiocesanInfo | null>(null);

  useEffect(() => {
    const loadDiocesan = async () => {
      try {
        const res = await fetchDiocesan();
        setDiocesan(res.data[0]);
      } catch (err) {
        console.error('Failed to fetch diocesan info:', err);
      }
    };
    loadDiocesan();
  }, []);

  return (
    <section className="py-24 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          
          {/* Image Section - Cinematic Framing */}
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-5 relative"
          >
            <div className="relative z-10 rounded-[2rem] overflow-hidden shadow-2xl aspect-[4/5] border-[12px] border-slate-50">
              <img
                src={diocesan?.photo || '/img/the_diocesan1.jpg'}
                alt={diocesan?.name || 'Bishop of Ibadan South'}
                className="w-full h-full object-cover grayscale-[20%] hover:grayscale-0 transition-all duration-700"
              />
            </div>
            
            {/* Decorative Background Elements */}
            <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-amber-500/10 rounded-full blur-3xl z-0" />
            <div className="absolute top-1/2 -left-8 w-24 h-48 bg-slate-900 rounded-full z-0 opacity-5" />
            
            {/* Floating Badge */}
            <div className="absolute bottom-10 -right-4 z-20 bg-white p-6 rounded-2xl shadow-xl border border-slate-100 hidden md:block">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-amber-100 rounded-xl text-amber-600">
                  <ShieldCheck size={24} />
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Episcopal</p>
                  <p className="text-sm font-bold text-slate-900">Diocesan Authority</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Text Section - Editorial Style */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-7 space-y-8 relative"
          >
            <Quote className="absolute -top-10 -left-6 w-20 h-20 text-slate-50 z-0" strokeWidth={1} />
            
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-px w-8 bg-amber-500" />
                <span className="text-[11px] font-bold uppercase tracking-[0.3em] text-amber-600">The Bishop's Desk</span>
              </div>
              
              <h2 className="text-4xl md:text-6xl font-serif font-bold text-slate-900 mb-8 leading-tight">
                A Warm Welcome <br />
                <span className="text-slate-400 font-serif italic">to our Diocese</span>
              </h2>

              <div className="space-y-6 text-slate-600 text-lg md:text-xl font-serif italic leading-relaxed border-l-4 border-amber-500/20 pl-8">
                <p>
                  "Greetings in the name of our Lord Jesus Christ. We are honored to have you here with us. Our mission remains steadfast—to spread the love of Christ and serve our community with compassion, faith, and integrity."
                </p>
                <p className="text-slate-500 text-base not-italic font-sans">
                  It is our hope that this website will not only meet your needs but also offer a window into the life and mission of our Diocese. As you explore, may God’s grace and peace accompany you.
                </p>
                <p className="text-slate-500 text-base not-italic font-sans">
                  We humbly ask for your prayers for our ministry as we pray for you and your family. May God’s blessings abound in your life.
                </p>
              </div>

              {/* Signature Area */}
              <div className="mt-12 pt-8 border-t border-slate-100 flex items-center gap-6">
                <div className="w-16 h-16 bg-slate-900 rounded-2xl flex items-center justify-center text-amber-500">
                   <Award size={32} />
                </div>
                <div>
                  <h4 className="text-2xl font-serif font-bold text-slate-900">
                    {diocesan?.name || 'Rt. Rev. AkinTunde Adedayo Popoola'}
                  </h4>
                  <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-amber-600 mt-1">
                    Bishop, Diocese of Ibadan South
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default BishopWelcome;